/**
 * Agent Mode Toggle Component
 * 
 * Allows users to switch between Answer mode and Action mode
 * for the AI orchestration system.
 */

import React from 'react';
import { Shield, Zap, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { AgentMode, getModeConfig } from '@/types/agentMode';

interface AgentModeToggleProps {
  currentMode: AgentMode;
  onModeChange: (mode: AgentMode) => void;
  disabled?: boolean;
  showDescription?: boolean;
}

export function AgentModeToggle({
  currentMode,
  onModeChange,
  disabled = false,
  showDescription = false
}: AgentModeToggleProps) {
  const answerConfig = getModeConfig('answer');
  const actionConfig = getModeConfig('action');
  const isAnswerMode = currentMode === 'answer';
  const isActionMode = currentMode === 'action';

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={isAnswerMode ? 'default' : 'outline'}
                size="sm"
                onClick={() => onModeChange('answer')}
                disabled={disabled}
                className={`flex items-center gap-2 ${
                  isAnswerMode 
                    ? 'bg-blue-500 hover:bg-blue-600 text-white' 
                    : 'border-blue-300 text-blue-600 hover:bg-blue-50'
                }`}
              >
                <Shield className="w-4 h-4" />
                <span>Answer</span>
                {isAnswerMode && (
                  <Badge variant="secondary" className="ml-1 text-xs">
                    Active
                  </Badge>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="font-semibold">Answer Mode</p>
              <p className="text-sm">Search and retrieve only</p>
              <p className="text-xs text-muted-foreground mt-1">
                Safe operations: No emails sent or data modified
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={isActionMode ? 'default' : 'outline'}
                size="sm"
                onClick={() => onModeChange('action')}
                disabled={disabled}
                className={`flex items-center gap-2 ${
                  isActionMode 
                    ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                    : 'border-orange-300 text-orange-600 hover:bg-orange-50'
                }`}
              >
                <Zap className="w-4 h-4" />
                <span>Action</span>
                {isActionMode && (
                  <Badge variant="secondary" className="ml-1 text-xs">
                    Active
                  </Badge>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="font-semibold">Action Mode</p>
              <p className="text-sm">Full execution capabilities</p>
              <p className="text-xs text-muted-foreground mt-1">
                Can send emails, create events, modify data
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="w-6 h-6">
                <Info className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              <div className="space-y-2">
                <div>
                  <p className="font-semibold text-blue-600">📖 Answer Mode</p>
                  <p className="text-xs">
                    • Search emails and calendar events<br />
                    • Analyze contacts and relationships<br />
                    • Generate summaries and insights<br />
                    • Extract action items<br />
                    ✅ No modifications or sends
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-orange-600">⚡ Action Mode</p>
                  <p className="text-xs">
                    • All Answer mode capabilities<br />
                    • <strong>Send emails via YOUR Google/Outlook</strong><br />
                    • Reply to emails with YOUR account<br />
                    • Create and update calendar events<br />
                    • Manage tasks and workflows<br />
                    ⚠️ Uses Gmail API / Microsoft Graph API directly<br />
                    🚫 Never uses Blink SDK for sending
                  </p>
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {showDescription && (
        <p className="text-xs text-muted-foreground">
          {isAnswerMode ? answerConfig.description : actionConfig.description}
        </p>
      )}
    </div>
  );
}
